import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { FiArrowLeft, FiMaximize, FiMinimize, FiVolume2, FiVolumeX, FiZap } from 'react-icons/fi';
import './PowerHourPage.css';

interface TVRep { name: string; dials: number; connects: number; meetings: number; coins: number; streak: number; }
interface TVPeriod { period: string; dials: number; connects: number; meetings: number; coins: number; reps: TVRep[]; }
interface TVPayload {
  server_time: string;
  mode: 'power' | 'idle';
  power: {
    name: string; multiplier: number; meeting_target: number;
    start: string; end: string; secs_left: number;
    reps: TVRep[]; team: { dials: number; connects: number; meetings: number; coins: number };
  } | null;
  next_power: { name: string; start: string; secs_to_start: number; multiplier: number } | null;
  next_boost: { name: string; start: string; end: string; secs_to_start: number; multiplier: number } | null;
  periods: Record<string, TVPeriod>;
  prizes: { individual: Record<string, string>; hustler?: Record<string, string> };
  milestones: { period: string; meetings: number; items: { meetings: number; reward: string; done: boolean }[] };
  teams_race: Record<string, { meetings: number; dials: number; coins: number; roster?: string[] }> | null;
  team_battle_prize?: string;
  awards?: { period: string; period_key: string; track: string; winner: string; value: number; prize: string; created_at: string }[];
  bonus_awards?: { id: string; name: string; coins: number; note: string; at: string; kind?: string; label?: string; amount?: number }[];
  all_time?: TVRep[];
  streak_leaders: { name: string; streak: number }[];
  active_multipliers: string[];
  on_floor?: string[];
}

const TRACK_LABEL: Record<string, string> = {
  champion: '🏆 Champion', closer: '🎯 Top Closer', hustler: '🔥 Hustler', streak: '🔥 Streak', team_battle: '⚔️ Team',
};

interface Particle { id: number; left: number; delay: number; hue: number; drift: number; size: number; }
interface FeedItem { id: number; kind: 'meeting' | 'connect'; rep: string; text: string; at: number; }
let tvSeq = 0;

const BANNER_MS = 30000;   // routine meeting banner — keep it up so the floor sees it
// rotating hype copy for a routine booked meeting (random pick each time)
const MEETING_LINES: ((n: string) => string)[] = [
  n => `🚀 ${n} BOOKED A MEETING!`,
  n => `🔒 ${n} BOOKED IT`,
  n => `🍳 ${n} IS COOKING`,
  n => `🪙 ${n} CASHED IN`,
  n => `📅 MEETING IN THE BAG — ${n}`,
  n => `💥 ANOTHER ONE FOR ${n}`,
  n => `🎯 ${n} LOCKED ONE IN`,
];
const MEETING_BANNER_VARIANTS = 3;   // ph-tv-banner-v0..v2
const FEED_MAX_AGE_MS = 15 * 60000;

const pad = (n: number) => String(Math.max(0, n)).padStart(2, '0');
const fmt = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};
const initials = (n: string) => n.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const repHue = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  return h;
};
const MEDALS = ['🥇', '🥈', '🥉'];
const PERIOD_LABEL: Record<string, string> = { day: 'TODAY', week: 'THIS WEEK', month: 'THIS MONTH', all: 'ALL-TIME' };
// the views that rotate through the Mission Control main panel
type BoardView = 'day' | 'week' | 'month' | 'all';
const BOARD_VIEWS: readonly BoardView[] = ['day', 'week', 'month', 'all'];
const VIEW_BTN_LABEL: Record<BoardView, string> = { day: 'TODAY', week: 'WEEK', month: 'MONTH', all: 'ALL-TIME' };

// The team-reward mountain: the climber ascends as meetings come in;
// each milestone is a flag on the slope that flips to ✅ when conquered.
const MilestoneMountain: React.FC<{
  meetings: number;
  items: { meetings: number; reward: string; done: boolean }[];
  max: number;
}> = ({ meetings, items, max }) => {
  const pathRef = useRef<SVGPathElement>(null);
  const [points, setPoints] = useState<{ frac: number; x: number; y: number }[]>([]);
  const [pathLen, setPathLen] = useState(0);
  const rawFrac = meetings / max; // uncapped — past the last flag we launch
  const climbFrac = Math.min(1, rawFrac);
  const fractions = JSON.stringify([
    climbFrac, ...items.map(i => Math.min(1, i.meetings / max)),
  ]);

  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;
    const len = path.getTotalLength();
    setPathLen(len);
    const fr: number[] = JSON.parse(fractions);
    setPoints(fr.map(f => {
      const pt = path.getPointAtLength(len * f);
      return { frac: f, x: pt.x, y: pt.y };
    }));
  }, [fractions]);

  let climber = points[0];
  if (climber && rawFrac > 1) {
    const over = Math.min(1, (rawFrac - 1) / 0.5);
    climber = { ...climber, x: climber.x + 50 * over, y: climber.y - 26 * over };
  }
  const flags = points.slice(1);

  return (
    <svg viewBox="0 0 880 260" className="ph-tv-mountain" preserveAspectRatio="xMidYMax meet">
      <defs>
        <linearGradient id="tvSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#160a36" />
          <stop offset="55%" stopColor="#2a1356" />
          <stop offset="100%" stopColor="#4a1d7a" />
        </linearGradient>
        <linearGradient id="tvBack" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4c2a87" /><stop offset="100%" stopColor="#2a1850" />
        </linearGradient>
        <linearGradient id="tvHill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#9d5cf6" /><stop offset="60%" stopColor="#6d28d9" />
          <stop offset="100%" stopColor="#241043" />
        </linearGradient>
        <linearGradient id="tvTrail" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#34d399" /><stop offset="60%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#f97316" />
        </linearGradient>
        <filter id="tvGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <radialGradient id="tvSun" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fef3c7" /><stop offset="70%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#fbbf24" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="880" height="260" fill="url(#tvSky)" rx="14" />
      {/* stars */}
      {[...Array(22)].map((_, i) => (
        <circle key={i} cx={(i * 127 + 37) % 880} cy={(i * 53 + 11) % 100}
          r={i % 4 === 0 ? 1.8 : 1} fill="#fff" opacity={0.5}>
          <animate attributeName="opacity" values="0.2;0.8;0.2"
            dur={`${2 + (i % 5)}s`} repeatCount="indefinite" />
        </circle>
      ))}
      <circle cx="800" cy="46" r="40" fill="url(#tvSun)" opacity="0.85" />
      <circle cx="800" cy="46" r="20" fill="#fde68a" />
      {/* back range (parallax) */}
      <path d="M 0 260 L 0 200 C 150 196, 280 150, 430 122 C 560 98, 660 120, 880 70 L 880 260 Z"
        fill="url(#tvBack)" opacity="0.6" />
      {/* main mountain — crisp ridgeline */}
      <path d="M 0 260 L 0 214 L 150 198 L 300 176 L 430 132 L 540 150 L 660 86 L 760 52 L 812 30 L 880 44 L 880 260 Z"
        fill="url(#tvHill)" stroke="#b794f6" strokeWidth="1" strokeOpacity="0.4" />
      {/* snow cap on the summit */}
      <path d="M 786 41 L 812 30 L 838 41 L 826 45 L 814 39 L 800 47 Z"
        fill="#f5f3ff" opacity="0.95" />
      {/* dashed full route */}
      <path ref={pathRef}
        d="M 28 210 L 150 196 L 300 174 L 430 130 L 540 148 L 660 84 L 760 50 L 810 30"
        fill="none" stroke="#fff" strokeWidth="2" strokeDasharray="7 9" opacity="0.35"
        strokeLinecap="round" strokeLinejoin="round" />
      {/* climbed-so-far glow trail */}
      {pathLen > 0 && (
        <path d={"M 28 210 L 150 196 L 300 174 L 430 130 L 540 148 L 660 84 L 760 50 L 810 30"}
          fill="none" stroke="url(#tvTrail)" strokeWidth="5" strokeLinecap="round"
          strokeLinejoin="round" filter="url(#tvGlow)"
          strokeDasharray={`${pathLen * climbFrac} ${pathLen}`} />
      )}
      {/* milestone flags — labels float in the sky above, clamped to the canvas.
          The NEXT unconquered flag is the star: gold, glowing, bigger label. */}
      {(() => {
        const nextMeetings = items.filter(it => !it.done)
          .sort((a, b) => a.meetings - b.meetings)[0]?.meetings;
        return flags.map((pt, i) => {
          const item = items[i];
          if (!item) return null;
          const isNext = item.meetings === nextMeetings;
          const labelText = `${item.meetings} · ${item.reward}`;
          const fontSize = isNext ? 14 : 12;
          const half = Math.min(190, labelText.length * (fontSize * 0.29));
          const lx = Math.min(874 - half, Math.max(6 + half, pt.x)) - pt.x;
          const ly = -38 - (i % 2) * 18; // stagger neighbors vertically
          return (
            <g key={i} transform={`translate(${pt.x}, ${pt.y})`} opacity={item.done ? 0.55 : 1}>
              <line x1="0" y1="2" x2="0" y2="-26" stroke="#e5e7eb" strokeWidth="2.5" />
              {item.done
                ? <path d="M 0 -26 L 22 -20 L 0 -14 Z" fill="#34d399" />
                : <path d="M 0 -26 L 22 -20 L 0 -14 Z"
                    fill={isNext ? '#fbbf24' : '#ef4444'}
                    filter={isNext ? 'url(#tvGlow)' : undefined}>
                    <animateTransform attributeName="transform" type="skewY"
                      values="0;5;0;-4;0" dur={isNext ? '1.1s' : '1.8s'} repeatCount="indefinite" />
                  </path>}
              <circle r={isNext ? 5 : 3.5} fill={item.done ? '#34d399' : '#fbbf24'}>
                {isNext && <animate attributeName="r" values="4;6;4" dur="1.4s" repeatCount="indefinite" />}
              </circle>
              <text x={lx} y={ly} textAnchor="middle" fontSize={fontSize} fontWeight="bold"
                fill={item.done ? '#34d399' : isNext ? '#fbbf24' : '#e9d5ff'}
                stroke="#160a36" strokeWidth="3" paintOrder="stroke">
                {item.done ? '✅ ' : isNext ? '🚩 ' : ''}{labelText}
              </text>
            </g>
          );
        });
      })()}
      {/* team climber */}
      {climber && (
        <g className="ph-climber" transform={`translate(${climber.x}, ${climber.y})`}>
          <ellipse cx="0" cy="16" rx="13" ry="3.5" fill="#000" opacity="0.3" />
          <circle r="14" fill="#fbbf24" stroke="#fff" strokeWidth="2.5" filter="url(#tvGlow)" />
          <text textAnchor="middle" dy="5.5" fontSize="15">
            {rawFrac > 1 ? '🚀' : rawFrac >= 1 ? '🏆' : '🧗'}
          </text>
          {/* count badge tucked by the climber, outlined for contrast */}
          <text x="0" y="34" textAnchor="middle" fontSize="13" fontWeight="900"
            fill="#fff" stroke="#160a36" strokeWidth="3" paintOrder="stroke">{meetings}</text>
        </g>
      )}
      {rawFrac > 1 && (
        <text x="858" y="20" textAnchor="middle" fontSize="20">🌟
          <animate attributeName="opacity" values="0.5;1;0.5" dur="1.2s" repeatCount="indefinite" />
        </text>
      )}
    </svg>
  );
};

// Slowly auto-scrolls its content when it overflows (gentle ping-pong), so a
// long leaderboard reveals everyone on the wall without shrinking the text.
const AutoScroll: React.FC<{ children: React.ReactNode; speed?: number }> = ({ children, speed = 14 }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0, pos = 0, dir = 1, pause = 100;
    const tick = () => {
      const max = el.scrollHeight - el.clientHeight;
      if (max > 2) {
        if (pause > 0) { pause--; }
        else {
          pos += (dir * speed) / 60;
          if (pos >= max) { pos = max; dir = -1; pause = 100; }
          else if (pos <= 0) { pos = 0; dir = 1; pause = 100; }
          el.scrollTop = pos;
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [speed]);
  return <div ref={ref} className="ph-autoscroll">{children}</div>;
};

// ===== celebration "moments" — one full-screen animation at a time =====
type CelebKind = 'jackpot' | 'firstblood' | 'firststrike' | 'summit' | 'hattrick' | 'comeback' | 'multibook' | 'onfire';
interface Celebration { id: number; kind: CelebKind; title: string; sub: string; }
const CELEB_PRIORITY: Record<CelebKind, number> = {
  jackpot: 120, firstblood: 100, summit: 90, hattrick: 85, firststrike: 75, comeback: 70, multibook: 60, onfire: 50,
};
const CELEB_MS: Record<CelebKind, number> = {
  jackpot: 30000, firstblood: 30000, summit: 30000, hattrick: 30000, firststrike: 30000, comeback: 30000, multibook: 30000, onfire: 30000,
};

// tiny WebAudio synth (no assets) — a distinct stinger per moment. Browsers
// block audio until a user gesture, so the context is unlocked on first click.
let tvAudioCtx: AudioContext | null = null;
const tvAudioUnlock = () => {
  try {
    if (!tvAudioCtx) tvAudioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (tvAudioCtx.state === 'suspended') tvAudioCtx.resume();
  } catch { /* best-effort */ }
};
const playTone = (freqs: number[], dur = 0.12, type: OscillatorType = 'square', gain = 0.07) => {
  try {
    tvAudioUnlock();
    const ctx = tvAudioCtx!;
    freqs.forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = type; o.frequency.value = f;
      g.gain.setValueAtTime(gain, ctx.currentTime + i * dur);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + (i + 1) * dur);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * dur);
      o.stop(ctx.currentTime + (i + 1) * dur + 0.02);
    });
  } catch { /* audio is best-effort */ }
};
const CELEB_SOUND: Record<CelebKind, () => void> = {
  jackpot:     () => playTone([523, 659, 784, 1047, 1319, 1568, 2093], 0.07, 'square', 0.08), // slot-machine win climb
  firstblood:  () => playTone([220, 165, 110, 98], 0.17, 'sawtooth', 0.09),     // ominous descent
  firststrike: () => playTone([1568, 1047], 0.06, 'sawtooth', 0.07),           // quick slash
  hattrick:    () => playTone([523, 659, 784], 0.13, 'triangle', 0.08),        // 3-note
  summit:      () => playTone([523, 659, 784, 1047, 1319], 0.12, 'triangle', 0.09), // fanfare
  comeback:    () => playTone([392, 523, 659, 880], 0.1, 'square', 0.07),      // rising surge
  multibook:   () => playTone([880, 1175, 880, 1319], 0.09, 'square', 0.07),   // double blip
  onfire:      () => playTone([659, 784, 988], 0.09, 'sawtooth', 0.07),
};
const sndMeeting = () => playTone([988, 1319], 0.1, 'square', 0.07);  // routine booking
// preview buttons (🎬 toggle in the topbar) so the moments can be tested on demand
const CELEB_TESTS: { kind: CelebKind; label: string; title: string; sub: (n: string) => string }[] = [
  { kind: 'jackpot', label: '🎰 Jackpot', title: '🎰 JACKPOT', sub: n => `${n} — +5,000 🪙!` },
  { kind: 'firstblood', label: '🩸 First Blood', title: '🩸 FIRST BLOOD', sub: n => `${n} — first meeting of the week!` },
  { kind: 'firststrike', label: '⚔️ First Strike', title: '⚔️ FIRST STRIKE', sub: n => `${n} — first meeting of the day` },
  { kind: 'hattrick', label: '🎩 Hat Trick', title: '🎩 HAT TRICK', sub: n => `${n} — 3 meetings today!` },
  { kind: 'onfire', label: '🔥 On Fire', title: '🔥 ON FIRE', sub: n => `${n} — 2 meetings today` },
  { kind: 'multibook', label: '⚡ Multi-Book', title: '⚡ DOUBLE BOOKING', sub: () => 'Boston & Samuel' },
  { kind: 'summit', label: '🏔️ Summit', title: '🏔️ SUMMIT REACHED', sub: () => 'Team unlocked Paintball!' },
  { kind: 'comeback', label: '🚀 Comeback', title: '🚀 COMEBACK', sub: n => `${n} storms into the lead!` },
];

// bespoke FX layer per moment — drawn with CSS/SVG, no emoji
const CelebFx: React.FC<{ kind: CelebKind }> = ({ kind }) => {
  switch (kind) {
    case 'jackpot':  // slot-machine win — a heavy curtain of coins raining down
      return (
        <>
          <div className="ph-fx-jackpot-glow" />
          <div className="ph-fx-coinfall">
            {Array.from({ length: 60 }).map((_, i) => (
              <span key={i} className="ph-fx-coin" style={{
                left: `${(i * 17 + 3) % 100}%`,
                animationDelay: `${(i % 12) * 0.18}s`,
                animationDuration: `${1.6 + (i % 5) * 0.32}s`,
                ['--spin' as any]: `${(i % 2 ? 1 : -1) * (360 + (i % 4) * 180)}deg`,
              }}>🪙</span>
            ))}
          </div>
        </>
      );
    case 'firstblood':  // Bond gun-barrel/sniper target + blood streaming down
      return (
        <>
          <div className="ph-fb-barrel" />
          <svg className="ph-fb-target" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid meet">
            <g fill="none" stroke="#fff" strokeWidth="1.6">
              <circle cx="100" cy="100" r="84" />
              <circle cx="100" cy="100" r="58" />
              <circle cx="100" cy="100" r="32" />
              <line x1="100" y1="2" x2="100" y2="50" />
              <line x1="100" y1="150" x2="100" y2="198" />
              <line x1="2" y1="100" x2="50" y2="100" />
              <line x1="150" y1="100" x2="198" y2="100" />
            </g>
            <circle cx="100" cy="100" r="5" fill="#ef4444" />
          </svg>
          <div className="ph-fb-blood">
            {Array.from({ length: 16 }).map((_, i) => (
              <span key={i} className="ph-fb-drip" style={{
                left: `${i * 6.4 + 1}%`, animationDelay: `${(i % 5) * 0.14}s`,
                ['--h' as any]: `${65 + (i * 17) % 35}%`,
              }} />
            ))}
          </div>
        </>
      );
    case 'firststrike':  // katana slash + flash — first meeting of the DAY
      return (
        <>
          <div className="ph-fx-flash" />
          <div className="ph-fx-slash" />
          <div className="ph-fx-slash ph-fx-slash2" />
        </>
      );
    case 'hattrick':  // arena spotlight rays + three goal lights igniting 1-2-3
      return (
        <>
          <div className="ph-fx-rays" />
          <div className="ph-fx-lights">
            {[0, 1, 2].map(i => <span key={i} style={{ animationDelay: `${i * 0.28}s` }} />)}
          </div>
        </>
      );
    case 'onfire':  // a wall of flame rising from the bottom + rising embers
      return (
        <>
          <div className="ph-fx-firewall" />
          <div className="ph-fx-embers">
            {Array.from({ length: 22 }).map((_, i) => (
              <span key={i} style={{ left: `${(i * 97 + 5) % 100}%`, animationDelay: `${(i % 7) * 0.18}s` }} />
            ))}
          </div>
        </>
      );
    case 'summit':  // fireworks bursting across the screen
      return (
        <div className="ph-fx-fireworks">
          {Array.from({ length: 7 }).map((_, i) => (
            <span key={i} className="ph-fw" style={{
              left: `${12 + (i * 13) % 76}%`, top: `${18 + (i * 29) % 50}%`,
              animationDelay: `${(i % 5) * 0.35}s`,
            }} />
          ))}
        </div>
      );
    case 'comeback':  // light streaks shooting upward (the surge)
      return (
        <div className="ph-fx-streaks">
          {Array.from({ length: 16 }).map((_, i) => (
            <span key={i} style={{ left: `${(i * 6 + 2) % 100}%`, animationDelay: `${(i % 6) * 0.1}s` }} />
          ))}
        </div>
      );
    case 'multibook':  // expanding shockwave rings
      return <div className="ph-fx-shock"><span /><span /><span /><span /></div>;
    default:
      return null;
  }
};

const CelebrationOverlay: React.FC<{ c: Celebration }> = ({ c }) => (
  <div className={`ph-celeb ph-celeb-${c.kind}`} style={{ ['--celeb-ms' as any]: `${CELEB_MS[c.kind]}ms` }}>
    <CelebFx kind={c.kind} />
    <div className="ph-celeb-card">
      <div className={`ph-celeb-title${c.kind === 'firstblood' ? ' ph-fb-text' : ''}`}>{c.title}</div>
      <div className="ph-celeb-sub">{c.sub}</div>
    </div>
  </div>
);

const PowerHourTV: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<TVPayload | null>(null);
  const [now, setNow] = useState(Date.now());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef<HTMLDivElement>(null);
  const fetchedAt = useRef(Date.now());

  // live-change celebration state
  const [particles, setParticles] = useState<Particle[]>([]);
  const [banner, setBanner] = useState<{ text: string; variant: number } | null>(null);
  const [shake, setShake] = useState(false);
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const [hotReps, setHotReps] = useState<Set<string>>(new Set());
  const prevDay = useRef<Map<string, TVRep> | null>(null);
  const bannerTimer = useRef<number | undefined>(undefined);
  const bannerRef = useRef<{ text: string; variant: number } | null>(null);
  // celebration director — one milestone "moment" plays at a time; a higher- or
  // equal-priority moment can't be interrupted by a smaller one
  const [celeb, setCeleb] = useState<Celebration | null>(null);
  const [showTest, setShowTest] = useState(false);
  const [sound, setSound] = useState(() => localStorage.getItem('ph-tv-sound') !== 'off');
  // which view leads the Mission Control board (persisted; survives the reload)
  const [boardPeriod, setBoardPeriod] = useState<BoardView>(() => {
    const s = localStorage.getItem('ph-tv-board-period');
    return (BOARD_VIEWS as readonly string[]).includes(s || '') ? s as BoardView : 'day';
  });
  const cycleBoardPeriod = () => setBoardPeriod(p => {
    const next = BOARD_VIEWS[(BOARD_VIEWS.indexOf(p) + 1) % BOARD_VIEWS.length];
    localStorage.setItem('ph-tv-board-period', next);
    return next;
  });
  const soundRef = useRef(sound);
  useEffect(() => { soundRef.current = sound; }, [sound]);
  // browsers block audio until a user gesture — unlock on the first click/tap
  useEffect(() => {
    const unlock = () => tvAudioUnlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => window.removeEventListener('pointerdown', unlock);
  }, []);
  const celebUntil = useRef(0);
  const celebTimer = useRef<number | undefined>(undefined);
  const prevWeekReps = useRef<Map<string, TVRep> | null>(null);
  const prevDoneFlags = useRef<Set<number> | null>(null);
  const seenBonus = useRef<Set<string> | null>(null);  // null until first paint seeds it

  const confettiBurst = useCallback((count = 110) => {
    const burst: Particle[] = Array.from({ length: count }, () => ({
      id: ++tvSeq,
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      hue: Math.floor(Math.random() * 360),
      drift: (Math.random() - 0.5) * 240,
      size: 6 + Math.random() * 9,
    }));
    setParticles(p => [...p, ...burst]);
    window.setTimeout(() => setParticles(p => p.filter(x => !burst.includes(x))), 4000);
  }, []);

  const fireCeleb = useCallback((kind: CelebKind, title: string, sub: string) => {
    const t = Date.now();
    setCeleb(prev => {
      if (prev && t < celebUntil.current && CELEB_PRIORITY[prev.kind] >= CELEB_PRIORITY[kind]) return prev;
      celebUntil.current = t + CELEB_MS[kind];
      window.clearTimeout(celebTimer.current);
      celebTimer.current = window.setTimeout(() => setCeleb(null), CELEB_MS[kind]);
      return { id: ++tvSeq, kind, title, sub };
    });
    confettiBurst(kind === 'firstblood' ? 260 : kind === 'hattrick' || kind === 'summit' ? 200 : 150);
    if (soundRef.current) CELEB_SOUND[kind]?.();
  }, [confettiBurst]);

  // diff each poll against the previous one and celebrate what changed
  useEffect(() => {
    const reps = data?.periods?.day?.reps;
    if (!reps) return;
    const next = new Map(reps.map(r => [r.name, r]));
    const prev = prevDay.current;
    prevDay.current = next;
    const weekNext = new Map((data?.periods?.week?.reps || []).map(r => [r.name, r] as const));
    const weekPrev = prevWeekReps.current;
    prevWeekReps.current = weekNext;
    if (!prev) return;  // first paint: seed only, no fireworks for old news

    const meetingNames: string[] = [];
    const connectNames: string[] = [];
    const dayFirst: string[] = [];  // rep's 1st meeting today
    const hatDay: string[] = [];    // crossed to 3 today
    const fireDay: string[] = [];   // crossed to 2 today
    next.forEach((r, name) => {
      const p = prev.get(name);
      const before = p?.meetings ?? 0;
      if (r.meetings > before) {
        meetingNames.push(name);
        if (before < 3 && r.meetings >= 3) hatDay.push(name);
        else if (before < 2 && r.meetings >= 2) fireDay.push(name);
        else if (before < 1 && r.meetings >= 1) dayFirst.push(name);
      } else if (p && r.connects > p.connects) {
        connectNames.push(name);
      }
    });

    // per-rep WEEK first meeting → FIRST BLOOD; per-rep week hat trick too
    const weekFirst: string[] = [];
    const hatWeek: string[] = [];
    if (weekPrev) {
      weekNext.forEach((r, name) => {
        const wb = weekPrev.get(name)?.meetings ?? 0;
        if (wb < 1 && r.meetings >= 1) weekFirst.push(name);
        if (wb < 3 && r.meetings >= 3) hatWeek.push(name);
      });
    }

    // team-reward mountain flags conquered → SUMMIT
    const doneNow = new Set((data?.milestones?.items || []).filter(i => i.done).map(i => i.meetings));
    const prevDone = prevDoneFlags.current;
    prevDoneFlags.current = doneNow;

    const nowMs = Date.now();
    const newFeed: FeedItem[] = [
      ...meetingNames.map(n => ({
        id: ++tvSeq, kind: 'meeting' as const, rep: n, at: nowMs,
        text: `🚀 ${n} BOOKED A MEETING`,
      })),
      ...connectNames.map(n => ({
        id: ++tvSeq, kind: 'connect' as const, rep: n, at: nowMs,
        text: `📞 ${n} connected`,
      })),
    ];
    if (newFeed.length) {
      // one chip per rep+kind — a repeat connect refreshes the timer, not a dupe
      const incoming = new Set(newFeed.map(x => `${x.kind}:${x.rep}`));
      setFeed(f => [...newFeed, ...f.filter(x => !incoming.has(`${x.kind}:${x.rep}`))].slice(0, 6));
      setHotReps(new Set([...meetingNames, ...connectNames]));
      window.setTimeout(() => setHotReps(new Set()), 12000);
    }

    // SUMMIT can fire without a fresh meeting this tick (flag flips on aggregate)
    if (prevDone) {
      const conquered = (data?.milestones?.items || []).find(i => i.done && !prevDone.has(i.meetings));
      if (conquered) fireCeleb('summit', '🏔️ SUMMIT REACHED', `Team unlocked ${conquered.reward}!`);
    }

    if (meetingNames.length) {
      setShake(true);
      window.setTimeout(() => setShake(false), 700);
      let moment = false;
      // each rep's FIRST meeting of the WEEK → First Blood
      for (const n of weekFirst) {
        fireCeleb('firstblood', '🩸 FIRST BLOOD', `${n} — first meeting of the week!`); moment = true;
      }
      // each rep's FIRST meeting of the DAY (when it isn't also their week-first) → First Strike
      for (const n of dayFirst) {
        if (!weekFirst.includes(n)) {
          fireCeleb('firststrike', '⚔️ FIRST STRIKE', `${n} — first meeting of the day`); moment = true;
        }
      }
      if (hatWeek.length) { fireCeleb('hattrick', '🎩 HAT TRICK', `${hatWeek[0]} — 3 meetings this week!`); moment = true; }
      else if (hatDay.length) { fireCeleb('hattrick', '🎩 HAT TRICK', `${hatDay[0]} — 3 meetings today!`); moment = true; }
      if (meetingNames.length >= 2) {
        fireCeleb('multibook', meetingNames.length >= 3 ? '⚡ TRIPLE BOOKING' : '⚡ DOUBLE BOOKING', meetingNames.join(' & ')); moment = true;
      } else if (fireDay.length) { fireCeleb('onfire', '🔥 ON FIRE', `${fireDay[0]} — 2 meetings today`); moment = true; }
      // comeback: a rep sitting 3rd-or-lower last tick storms to #1 this tick
      const newLeader = Array.from(next.keys())[0];
      if (newLeader && meetingNames.includes(newLeader) && Array.from(prev.keys()).indexOf(newLeader) >= 2
          && (next.get(newLeader)?.meetings || 0) > 0) {
        fireCeleb('comeback', '🚀 COMEBACK', `${newLeader} storms into the lead!`); moment = true;
      }
      if (!moment) {
        confettiBurst(110);
        if (soundRef.current) sndMeeting();
        const who = meetingNames.join(' & ');
        const text = MEETING_LINES[Math.floor(Math.random() * MEETING_LINES.length)](who);
        const variant = Math.floor(Math.random() * MEETING_BANNER_VARIANTS);
        setBanner({ text, variant });
        window.clearTimeout(bannerTimer.current);
        bannerTimer.current = window.setTimeout(() => setBanner(null), BANNER_MS);
      }
    }
  }, [data, confettiBurst, fireCeleb]);

  // one-off bonus coin awards → JACKPOT. Seed seen-ids on first paint so a TV
  // reload never replays an award that already happened.
  useEffect(() => {
    const awards = data?.bonus_awards || [];
    if (seenBonus.current === null) {
      seenBonus.current = new Set(awards.map(a => a.id));
      return;
    }
    for (const a of awards) {
      if (seenBonus.current.has(a.id)) continue;
      seenBonus.current.add(a.id);
      let title = '🎰 JACKPOT';
      let sub: string;
      if (a.kind === 'gift') {
        title = '💳 GIFT CARD';
        sub = `${a.name} — $${(a.amount || 0).toLocaleString()} gift card!${a.note ? ` · ${a.note}` : ''}`;
      } else if (a.kind === 'prize') {
        title = '🏅 PRIZE UNLOCKED';
        sub = `${a.name} — ${a.label || 'prize won'}${a.note ? ` → 🎁 ${a.note}` : ''}`;
      } else if (a.kind === 'redeem') {
        // spending coins deserves as loud a moment as winning them — the
        // floor seeing rewards actually land is what makes saving worth it
        title = '🛍️ REDEEMED';
        sub = `${a.name} — ${a.label || 'a reward'}${a.coins ? ` · ${a.coins.toLocaleString()} 🪙` : ''}`;
      } else if (a.kind === 'boost') {
        title = '🚀 POWER-UP';
        sub = `${a.name} — ${a.label || 'boosted'}${a.note ? ` · ${a.note}` : ''} · clear the runway!`;
      } else if (a.kind === 'wheel') {
        title = '🎡 WHEEL SPIN';
        sub = `${a.name} spun the Mystery Wheel — ${a.label || 'a prize'}${a.note ? ` · ${a.note}` : ''}!`;
      } else if (a.kind === 'rush') {
        title = '⚡🎁 FUEL RUSH';
        sub = `${a.name} bought the WHOLE FLOOR ${a.label || 'a Fuel Rush'}${a.note ? ` · ${a.note}` : ''} — phones up!`;
      } else {
        sub = a.note ? `${a.name} — +${a.coins.toLocaleString()} 🪙 · ${a.note}`
                     : `${a.name} — +${a.coins.toLocaleString()} 🪙!`;
      }
      fireCeleb('jackpot', title, sub);
    }
  }, [data, fireCeleb]);

  // age out stale feed entries
  useEffect(() => {
    const t = window.setInterval(
      () => setFeed(f => f.filter(x => Date.now() - x.at < FEED_MAX_AGE_MS)), 30000);
    return () => window.clearInterval(t);
  }, []);

  // layout experiment: 'a' = full-screen rotating scenes, 'c' = scoreboard
  // (permanent big board + rotating sidebar), 'classic' = original dashboard
  // The wall is standardized on Mission Control (B); A/C/classic are hidden from
  // the UI but still reachable via ?layout= for debugging.
  const [layout] = useState<'classic' | 'a' | 'b' | 'c'>(() => {
    const q = new URLSearchParams(window.location.search).get('layout');
    if (q === 'a' || q === 'b' || q === 'c' || q === 'classic') return q;
    return 'b';
  });
  const [scene, setScene] = useState(0);          // layout A rotation
  const [sideIdx, setSideIdx] = useState(0);      // layout C sidebar rotation
  useEffect(() => {
    if (layout !== 'a') return;
    const t = window.setInterval(() => setScene(s => (s + 1) % 4), 15000);
    return () => window.clearInterval(t);
  }, [layout]);
  useEffect(() => {
    if (layout !== 'c') return;
    const t = window.setInterval(() => setSideIdx(s => (s + 1) % 4), 12000);
    return () => window.clearInterval(t);
  }, [layout]);
  useEffect(() => { if (banner) setScene(0); }, [banner]);  // celebration → board scene

  // scale-to-fit (classic layout only)
  const fitToScreen = useCallback(() => {
    const page = pageRef.current, inner = scaleRef.current;
    if (!page || !inner) return;
    if (layout !== 'classic') { inner.style.transform = 'none'; return; }
    const avail = page.clientHeight - 24;       // page padding allowance
    const need = inner.scrollHeight;             // unaffected by transform
    const s = need > 0 ? Math.min(1, avail / need) : 1;
    inner.style.transform = s < 1 ? `scale(${s})` : 'none';
    inner.style.transformOrigin = 'top center';
  }, [layout]);

  useEffect(() => {
    fitToScreen();
    window.addEventListener('resize', fitToScreen);
    document.addEventListener('fullscreenchange', fitToScreen);
    return () => {
      window.removeEventListener('resize', fitToScreen);
      document.removeEventListener('fullscreenchange', fitToScreen);
    };
  }, [fitToScreen]);
  useEffect(() => { fitToScreen(); });  // refit after every data render

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/power-hour/tv`);
      const d = await res.json();
      fetchedAt.current = Date.now();
      setData(d);
    } catch { /* keep showing last data */ }
  }, []);

  // weekly ⛽ Fuel per rep, same source as The Vault. The wallet query is
  // heavier than /tv and fuel only moves on dials/boosts, so poll it slowly.
  const [fuelBy, setFuelBy] = useState<Record<string, number>>({});
  const loadFuel = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/power-hour/market/summary`);
      const m: { reps?: { name: string; fuel?: number }[] } = await res.json();
      const next: Record<string, number> = {};
      for (const r of m.reps || []) if ((r.fuel ?? 0) > 0) next[r.name] = r.fuel!;
      setFuelBy(next);
    } catch { /* keep showing last fuel */ }
  }, []);
  useEffect(() => {
    loadFuel();
    const t = window.setInterval(loadFuel, 30000);
    return () => window.clearInterval(t);
  }, [loadFuel]);

  useEffect(() => {
    load();
    const p = window.setInterval(load, 5000);
    const t = window.setInterval(() => setNow(Date.now()), 500);
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => { window.clearInterval(p); window.clearInterval(t); document.removeEventListener('fullscreenchange', onFs); };
  }, [load]);

  // keep a ref of the banner so the daily reloader can see it without re-subscribing
  useEffect(() => { bannerRef.current = banner; }, [banner]);

  // NOTE: no automatic full-page reload. A window.location.reload() drops the
  // wall out of fullscreen (unless launched in kiosk mode), so we refresh the
  // TV manually when a deploy needs to be picked up. Live data still updates
  // via the 5s data poll above — that re-renders in place without leaving
  // fullscreen.

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else pageRef.current?.requestFullscreen();
  };

  const drift = Math.floor((now - fetchedAt.current) / 1000);
  const power = data?.mode === 'power' ? data.power : null;
  const openEnded = !!power && power.secs_left < 0;
  const secsLeft = power && !openEnded ? Math.max(0, power.secs_left - drift) : 0;
  const elapsed = power ? Math.max(0, Math.floor((now - new Date(power.start).getTime()) / 1000)) : 0;
  const secsToStart = data?.next_power ? Math.max(0, data.next_power.secs_to_start - drift) : 0;
  const prizes: { individual: Record<string, string>; hustler?: Record<string, string> } =
    data?.prizes || { individual: {}, hustler: {} };
  const milestones = data?.milestones;
  const maxMilestone = milestones?.items?.length
    ? Math.max(...milestones.items.map(i => i.meetings)) : 1;

  // the one board that matters: live block if running, else today
  const dayPd = data?.periods?.day;
  const usePowerReps = !!(power && power.reps?.length);
  const boardReps = usePowerReps ? power!.reps : (dayPd?.reps || []);
  const boardTitle = power ? power.name : "TODAY'S BOARD";
  // score must mirror whichever reps are shown — a live block with no in-window
  // calls falls back to today's rows, so it must show today's count too (not 0/5)
  const boardScore = power
    ? `🎯 ${usePowerReps ? power!.team.meetings : (dayPd?.meetings ?? 0)}/${power.meeting_target}`
    : `🎯 ${dayPd?.meetings ?? 0}`;
  const boardClock = power ? (openEnded ? `▶ ${fmt(elapsed)}` : fmt(secsLeft)) : '';

  const boardRows = (reps: TVRep[]) => (
    <>
      {reps.length === 0 && <div className="ph-empty">First dial takes the early lead… ☎️</div>}
      {reps.map((r, i) => (
        <div key={r.name}
          className={`ph-tvx-row ${i === 0 && r.coins > 0 ? 'leader' : ''} ${hotReps.has(r.name) ? 'hot' : ''}`}>
          <span className="ph-tvx-rank">{MEDALS[i] || i + 1}</span>
          <span className="ph-tvx-name">
            <span className="ph-avatar" style={{ background: `hsl(${repHue(r.name)}, 70%, 45%)` }}>{initials(r.name)}</span>
            <span className="ph-tvx-name-text">{r.name}</span>
            {r.streak >= 2 && <span className="ph-streak">🔥{r.streak}</span>}
          </span>
          <span className="ph-tvx-mtgs">{r.meetings > 0
            ? <>{'⭐'.repeat(Math.min(r.meetings, 4))} <b>{r.meetings}</b></>
            : <span className="ph-mtg-zero">–</span>}</span>
          <span className="ph-tvx-sub">{r.dials} dials · {r.connects} conn</span>
          <span className="ph-tvx-coins">
            <span>🪙 {r.coins.toLocaleString()}</span>
            {fuelBy[r.name] > 0 && <span className="ph-tv-fuel">⛽ {fuelBy[r.name].toLocaleString()}</span>}
          </span>
        </div>
      ))}
    </>
  );

  // the period's reward(s) — shown on whichever board/card displays that timeframe
  const boardPrize = (period: 'day' | 'week' | 'month') => {
    const c = prizes.individual?.[period];
    const h = prizes.hustler?.[period];
    if (!c && !h) return null;
    return (
      <div className="ph-tvx-board-prize">
        {c && <span>🏆 CHAMPION: {c}</span>}
        {h && <span className="ph-tvx-board-prize-h">🎯 TOP CLOSER: {h}</span>}
      </div>
    );
  };

  // today / live-power-block big board (used by layouts A and C, and B's "day")
  const bigBoard = (
    <div className="ph-tvx-board">
      <div className="ph-tvx-board-head">
        <span className="ph-tvx-board-title">{boardTitle}</span>
        {boardClock && <span className="ph-tvx-board-clock">{boardClock}</span>}
        <span className="ph-tvx-board-score">{boardScore}</span>
      </div>
      {boardPrize('day')}
      <div className="ph-tvx-rows">{boardRows(boardReps)}</div>
    </div>
  );

  // a big board for ANY view — 'day' reuses the live/today board above
  const periodBoard = (period: BoardView) => {
    if (period === 'day') return bigBoard;
    if (period === 'all') {
      const reps = data?.all_time || [];
      const total = reps.reduce((a, r) => a + r.coins, 0);
      return (
        <div className="ph-tvx-board">
          <div className="ph-tvx-board-head">
            <span className="ph-tvx-board-title">🪙 ALL-TIME COINS</span>
            <span className="ph-tvx-board-score">🪙 {total.toLocaleString()}</span>
          </div>
          {reps.length === 0
            ? <div className="ph-empty">No coins banked yet — the career board fills as calls roll in.</div>
            : <div className="ph-tvx-rows">{boardRows(reps)}</div>}
        </div>
      );
    }
    const pd = data?.periods?.[period];
    return (
      <div className="ph-tvx-board">
        <div className="ph-tvx-board-head">
          <span className="ph-tvx-board-title">{period === 'week' ? 'THIS WEEK' : 'THIS MONTH'}</span>
          <span className="ph-tvx-board-score">🎯 {pd?.meetings ?? 0}</span>
        </div>
        {boardPrize(period)}
        <div className="ph-tvx-rows">{boardRows(pd?.reps || [])}</div>
      </div>
    );
  };

  const topbar = (
    <div className="ph-topbar">
      <div className="ph-logo">
        {!isFullscreen && (
          <button className="ph-icon-btn" onClick={() => navigate('/power-hour')} title="Back to lobby">
            <FiArrowLeft />
          </button>
        )}
        <FiZap /> EARN THE SUMMIT
      </div>
      <div className="ph-tv-mults">
        {(data?.active_multipliers || []).map(m => (
          <span key={m} className="ph-mult-chip">⚡ {m}</span>
        ))}
      </div>
      <div className="ph-topbar-actions">
        {layout === 'b' && (
          <button className="ph-layout-btn ph-period-btn" onClick={cycleBoardPeriod}
            title="Rotate the prominent view (saved)">
            {VIEW_BTN_LABEL[boardPeriod]}
          </button>
        )}
        <button className="ph-icon-btn" title="Sound"
          onClick={() => { tvAudioUnlock(); const n = !sound; setSound(n); localStorage.setItem('ph-tv-sound', n ? 'on' : 'off'); }}>
          {sound ? <FiVolume2 /> : <FiVolumeX />}
        </button>
        <button className={`ph-icon-btn ${showTest ? 'on' : ''}`} onClick={() => setShowTest(s => !s)}
          title="Test celebration moments">🎬</button>
        <span className="ph-tv-clock">
          {new Date(now).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </span>
        <button className="ph-icon-btn" onClick={toggleFullscreen} title="Fullscreen">
          {isFullscreen ? <FiMinimize /> : <FiMaximize />}
        </button>
      </div>
    </div>
  );

  const onFloor = data?.on_floor || [];
  const floorStrip = onFloor.length > 0 ? (
    <div className="ph-tv-floor">
      <span className="ph-tv-floor-label">📞 ON THE FLOOR</span>
      {onFloor.slice(0, 10).map(n => (
        <span key={n} className="ph-tv-floor-rep">
          <span className="ph-avatar ph-avatar-sm" style={{ background: `hsl(${repHue(n)}, 70%, 45%)` }}>{initials(n)}</span>
          {n}
        </span>
      ))}
    </div>
  ) : null;

  const liveFeed = feed.length > 0 ? (
    <div className="ph-tv-feed">
      <span className="ph-tv-feed-label">🔴 LIVE</span>
      {feed.map(f => (
        <span key={f.id} className={`ph-tv-feed-item ${f.kind}`}>
          {f.text}
          <em>{Math.max(0, Math.round((now - f.at) / 60000))}m ago</em>
        </span>
      ))}
    </div>
  ) : null;

  const powerBlock = power && (
        <div className="ph-tv-power">
          <div className="ph-tv-power-head">
            <span className="ph-tv-power-name">
              {power.name}{power.multiplier > 1 ? ` — ${power.multiplier}x EVERYTHING` : ' — LIVE'}
            </span>
            <span className="ph-tv-power-clock">{openEnded ? `▶ ${fmt(elapsed)}` : fmt(secsLeft)}</span>
            <span className="ph-tv-power-score">
              🎯 {power.team.meetings}<em>/{power.meeting_target}</em>
            </span>
          </div>
          <div className="ph-progressbar">
            <div className="ph-progressfill"
              style={{ width: `${Math.min(100, (power.team.meetings / power.meeting_target) * 100)}%` }} />
          </div>
          <div className="ph-tv-power-board">
            {power.reps.length === 0 && <div className="ph-empty">First dial takes the early lead… ☎️</div>}
            {power.reps.map((r, i) => (
              <div key={r.name} className={`ph-tv-power-row ${i === 0 && r.coins > 0 ? 'leader' : ''} ${hotReps.has(r.name) ? 'hot' : ''}`}>
                <span className="ph-period-rank">{MEDALS[i] || i + 1}</span>
                <span className="ph-period-name">
                  <span className="ph-avatar" style={{ background: `hsl(${repHue(r.name)}, 70%, 45%)` }}>{initials(r.name)}</span>
                  {r.name}
                  {r.streak >= 2 && <span className="ph-streak">🔥{r.streak}</span>}
                </span>
                <span className="ph-period-mtgs">{r.meetings > 0 ? <>{'⭐'.repeat(Math.min(r.meetings, 4))} <b>{r.meetings}</b></> : <span className="ph-mtg-zero">–</span>}</span>
                <span className="ph-period-sub">{r.dials} dials · {r.connects} connects</span>
                <span className="ph-period-coins">
                  🪙 {r.coins.toLocaleString()}
                  {fuelBy[r.name] > 0 && <span className="ph-tv-fuel">⛽ {fuelBy[r.name].toLocaleString()}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      );

  const nextPowerBlock = !power && data?.next_power && (
        <div className="ph-tv-next">
          <div className="ph-tv-next-label">NEXT {data.next_power.name}</div>
          <div className="ph-tv-next-clock">{fmt(secsToStart)}</div>
          <div className="ph-tv-next-sub">
            {new Date(data.next_power.start).toLocaleString('en-US', {
              weekday: 'long', hour: 'numeric', minute: '2-digit',
            })}
            {data.next_power.multiplier > 1
              ? ` · ALL COINS ${data.next_power.multiplier}x ⚡`
              : ' · RALLY THE FLOOR 📞'}
          </div>
        </div>
      );

  const boostAd = data?.next_boost && (
        <div className="ph-tv-boost-ad">
          <span className="ph-tv-boost-label">⏭ NEXT BOOST</span>
          <span className="ph-tv-boost-name">{data.next_boost.name}</span>
          <span className="ph-tv-boost-when">
            {(() => {
              const s = new Date(data.next_boost.start);
              const e = new Date(data.next_boost.end);
              const today = new Date(now).toDateString() === s.toDateString();
              const tmrw = new Date(now + 86400000).toDateString() === s.toDateString();
              const day = today ? 'TODAY' : tmrw ? 'TOMORROW' : s.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
              const t = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
              return `${day} ${t(s)}–${t(e)}`;
            })()}
          </span>
          <span className="ph-tv-boost-mult">{data.next_boost.multiplier}x CONNECTS & MEETINGS</span>
          {data.next_boost.secs_to_start < 3600 && (
            <span className="ph-tv-boost-soon">⏱ {fmt(Math.max(0, data.next_boost.secs_to_start - drift))}</span>
          )}
        </div>
      );

  const periodGrid = (
      <div className="ph-tv-grid">
        {(['day', 'week', 'month'] as const).map(p => {
          const pd = data?.periods?.[p];
          const prize = prizes.individual?.[p];
          return (
            <div key={p} className="ph-tv-card">
              <div className="ph-tv-card-head">
                <span>{PERIOD_LABEL[p]}</span>
                <span className="ph-tv-card-mtgs">🎯 {pd?.meetings ?? 0}</span>
              </div>
              {prize && <div className="ph-tv-prize">🏆 CHAMPION: {prize}</div>}
              {prizes.hustler?.[p] && <div className="ph-tv-prize ph-tv-prize-hustler">🎯 TOP CLOSER: {prizes.hustler[p]}</div>}
              {(pd?.reps || []).map((r, i) => (
                <div key={r.name} className={`ph-tv-mini-row ${hotReps.has(r.name) ? 'hot' : ''}`}>
                  <span>{MEDALS[i] || i + 1}</span>
                  <span className="ph-tv-mini-name">{r.name}{r.streak >= 2 ? ` 🔥${r.streak}` : ''}</span>
                  <span className="ph-tv-mini-mtgs">{r.meetings > 0 ? `⭐${r.meetings}` : '–'}</span>
                  <span className="ph-tv-mini-coins">
            🪙{r.coins.toLocaleString()}
            {fuelBy[r.name] > 0 && <span className="ph-tv-fuel">⛽{fuelBy[r.name].toLocaleString()}</span>}
          </span>
                </div>
              ))}
              {(!pd || pd.reps.length === 0) && <div className="ph-admin-hint">No calls yet.</div>}
              <div className="ph-tv-card-foot">{pd?.dials ?? 0} dials · {pd?.connects ?? 0} connects</div>
            </div>
          );
        })}
      </div>
  );

  const mountainBlock = milestones && milestones.items.length > 0 && (
        <div className="ph-tv-milestones">
          <div className="ph-tv-card-head">
            <span>🏔️ TEAM REWARD MOUNTAIN</span>
            {(() => {
              const next = milestones.items
                .filter(i => !i.done)
                .sort((a, b) => a.meetings - b.meetings)[0];
              if (!next) return <span className="ph-tv-next-flag">🚀 ALL FLAGS CONQUERED — beyond the summit!</span>;
              const gap = next.meetings - milestones.meetings;
              return (
                <span className="ph-tv-next-flag">
                  🚩 NEXT FLAG: <b>{next.reward}</b> — {gap} meeting{gap === 1 ? '' : 's'} to go
                </span>
              );
            })()}
          </div>
          <MilestoneMountain meetings={milestones.meetings} items={milestones.items} max={maxMilestone} />
        </div>
      );

  const teamBattleCard = (
    <>
      {data && !data.teams_race && (
        <div className="ph-tv-card ph-tv-race">
          <div className="ph-tv-card-head"><span>⚔️ TEAM BATTLE</span></div>
          <div className="ph-admin-hint">No teams yet — assign rosters in ⚙️ Config and the battle bars appear here.</div>
        </div>
      )}
      {data?.teams_race && (
        <div className="ph-tv-card ph-tv-race">
          <div className="ph-tv-card-head">
            <span>⚔️ TEAM BATTLE ({PERIOD_LABEL[milestones?.period || 'month']})</span>
            {data.team_battle_prize && <span className="ph-tv-prize-inline">🎁 {data.team_battle_prize}</span>}
          </div>
          {Object.entries(data.teams_race)
            .sort((a, b) => b[1].meetings - a[1].meetings)
            .map(([team, s], i) => {
              const total = Object.values(data.teams_race!).reduce((a, x) => a + x.meetings, 0) || 1;
              return (
                <div key={team} className="ph-tv-race-row">
                  <span className="ph-tv-race-team">{i === 0 && s.meetings > 0 ? '👑 ' : ''}{team}</span>
                  <div className="ph-history-bar">
                    <div className="ph-history-fill" style={{ width: `${(s.meetings / total) * 100}%` }} />
                  </div>
                  <span className="ph-tv-race-score">🎯 {s.meetings}</span>
                </div>
              );
            })}
        </div>
      )}
    </>
  );

  const streaksCard = (data?.streak_leaders?.length || 0) > 0 ? (
    <div className="ph-tv-card">
      <div className="ph-tv-card-head"><span>🔥 STREAKS</span></div>
      {data!.streak_leaders.map(s => (
        <div key={s.name} className="ph-tv-mini-row">
          <span>🔥</span>
          <span className="ph-tv-mini-name">{s.name}</span>
          <span className="ph-tv-mini-coins">{s.streak} days</span>
        </div>
      ))}
    </div>
  ) : null;

  const battleStreaks = (
      <div className="ph-tv-bottom">
        {teamBattleCard}
        {streaksCard}
      </div>
  );

  const hofBlock = (data?.awards?.length || 0) > 0 && (
        <div className="ph-tv-card ph-tv-hof">
          <div className="ph-tv-card-head"><span>🏅 HALL OF FAME</span></div>
          <div className="ph-tv-hof-row">
            {data!.awards!.map((a, i) => (
              <div key={i} className="ph-tv-hof-chip">
                <span className="ph-tv-hof-track">{TRACK_LABEL[a.track] || a.track}</span>
                <span className="ph-tv-hof-winner">{a.winner}</span>
                <span className="ph-tv-hof-meta">{a.period_key}{a.prize ? ` · ${a.prize}` : ''}</span>
              </div>
            ))}
          </div>
        </div>
      );

  // ======== LAYOUT A: full-screen rotating scenes ========
  const scenes = [
    { key: 'board', el: <div className="ph-tva-scene">{bigBoard}</div> },
    { key: 'mountain', el: <div className="ph-tva-scene ph-tva-center">{mountainBlock || <div className="ph-empty">Set milestones in ⚙️ Config</div>}</div> },
    { key: 'warroom', el: <div className="ph-tva-scene">{battleStreaks}{hofBlock}</div> },
    { key: 'upnext', el: <div className="ph-tva-scene ph-tva-center">{nextPowerBlock}{boostAd}{periodGrid}</div> },
  ];
  const layoutA = (
    <div className="ph-tva">
      {scenes[scene].el}
      <div className="ph-tva-dots">
        {scenes.map((s, i) => (
          <button key={s.key} className={`ph-tva-dot ${i === scene ? 'on' : ''}`}
            onClick={() => setScene(i)} />
        ))}
      </div>
    </div>
  );

  // ======== LAYOUT C: scoreboard — permanent board + rotating sidebar ========
  const sideCards = [
    mountainBlock || periodGrid,
    battleStreaks,
    <>{periodGrid}</>,
    <>{nextPowerBlock}{boostAd}{hofBlock}</>,
  ];
  const layoutC = (
    <div className="ph-tvc">
      <div className="ph-tvc-left">{bigBoard}</div>
      <div className="ph-tvc-right">
        {!power && data?.next_power && (
          <div className="ph-tvc-countdown">
            NEXT {data.next_power.name} · <b>{fmt(secsToStart)}</b>
          </div>
        )}
        <div className="ph-tvc-rotor" key={sideIdx}>{sideCards[sideIdx % sideCards.length]}</div>
        <div className="ph-tva-dots">
          {sideCards.map((_, i) => (
            <button key={i} className={`ph-tva-dot ${i === sideIdx % sideCards.length ? 'on' : ''}`}
              onClick={() => setSideIdx(i)} />
          ))}
        </div>
      </div>
    </div>
  );

  // ======== LAYOUT B: Mission Control — everything on one screen, no rotation ========
  // a compact rail card with a slow auto-scrolling roster (week / month / all-time)
  const miniRows = (reps: TVRep[]) => (
    <AutoScroll>
      {reps.map((r, i) => (
        <div key={r.name} className={`ph-tv-mini-row ${hotReps.has(r.name) ? 'hot' : ''}`}>
          <span>{MEDALS[i] || i + 1}</span>
          <span className="ph-tv-mini-name">{r.name}{r.streak >= 2 ? ` 🔥${r.streak}` : ''}</span>
          <span className="ph-tv-mini-mtgs">{r.meetings > 0 ? `⭐${r.meetings}` : '–'}</span>
          <span className="ph-tv-mini-coins">
            🪙{r.coins.toLocaleString()}
            {fuelBy[r.name] > 0 && <span className="ph-tv-fuel">⛽{fuelBy[r.name].toLocaleString()}</span>}
          </span>
        </div>
      ))}
    </AutoScroll>
  );
  const periodCardB = (p: BoardView) => {
    if (p === 'all') {
      const reps = data?.all_time || [];
      const total = reps.reduce((a, r) => a + r.coins, 0);
      return (
        <div className="ph-tv-card ph-tvb-pcard">
          <div className="ph-tv-card-head">
            <span>🪙 ALL-TIME</span>
            <span className="ph-tv-card-mtgs">🪙 {total.toLocaleString()}</span>
          </div>
          {reps.length === 0
            ? <div className="ph-admin-hint">No coins banked yet.</div>
            : miniRows(reps)}
          <div className="ph-tv-card-foot">career coin totals · all time</div>
        </div>
      );
    }
    const pd = data?.periods?.[p];
    const prize = prizes.individual?.[p];
    return (
      <div className="ph-tv-card ph-tvb-pcard">
        <div className="ph-tv-card-head">
          <span>{PERIOD_LABEL[p]}</span>
          <span className="ph-tv-card-mtgs">🎯 {pd?.meetings ?? 0}</span>
        </div>
        {prize && <div className="ph-tv-prize">🏆 CHAMPION: {prize}</div>}
        {prizes.hustler?.[p] && <div className="ph-tv-prize ph-tv-prize-hustler">🎯 TOP CLOSER: {prizes.hustler[p]}</div>}
        {(!pd || pd.reps.length === 0)
          ? <div className="ph-admin-hint">No calls yet.</div>
          : miniRows(pd.reps)}
        <div className="ph-tv-card-foot">{pd?.dials ?? 0} dials · {pd?.connects ?? 0} connects</div>
      </div>
    );
  };

  const layoutB = (
    <div className="ph-tvb">
      <div className="ph-tvb-board">
        {!power && data?.next_power && (
          <div className="ph-tvc-countdown">NEXT {data.next_power.name} · <b>{fmt(secsToStart)}</b></div>
        )}
        {periodBoard(boardPeriod)}
      </div>
      <div className="ph-tvb-rail">
        {BOARD_VIEWS
          .filter(p => p !== boardPeriod)
          .map(p => <React.Fragment key={p}>{periodCardB(p)}</React.Fragment>)}
      </div>
      <div className="ph-tvb-foot">
        {mountainBlock}
        {teamBattleCard}
        {streaksCard}
      </div>
    </div>
  );

  return (
    <div className={`ph-page ph-tv ph-tv-${layout} ${shake ? 'ph-shake' : ''}`} ref={pageRef}>
      {/* confetti overlay */}
      <div className="ph-confetti">
        {particles.map(p => (
          <span key={p.id} className="ph-particle"
            style={{
              left: `${p.left}%`, animationDelay: `${p.delay}s`,
              background: `hsl(${p.hue}, 95%, 60%)`,
              width: p.size, height: p.size * 0.45,
              ['--drift' as any]: `${p.drift}px`,
            }} />
        ))}
      </div>
      {banner && <div className={`ph-banner ph-tv-banner ph-tv-banner-v${banner.variant}`}>{banner.text}</div>}
      {celeb && <CelebrationOverlay c={celeb} />}
      {showTest && (
        <div className="ph-celeb-testbar">
          <span className="ph-celeb-testbar-label">🎬 Preview moments</span>
          {CELEB_TESTS.map(t => (
            <button key={t.kind} className="ph-mini-cta"
              onClick={() => fireCeleb(t.kind, t.title, t.sub(data?.periods?.day?.reps?.[0]?.name || 'Boston Funk'))}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {layout === 'classic' && (
        <div className="ph-tv-scale" ref={scaleRef}>
          {topbar}
          {floorStrip}
          {liveFeed}
          {powerBlock}
          {nextPowerBlock}
          {boostAd}
          {periodGrid}
          {mountainBlock}
          {battleStreaks}
          {hofBlock}
        </div>
      )}
      {layout === 'b' && (<>{topbar}{floorStrip}{liveFeed}{layoutB}</>)}
      {layout === 'a' && (<>{topbar}{floorStrip}{liveFeed}{layoutA}</>)}
      {layout === 'c' && (<>{topbar}{floorStrip}{liveFeed}{layoutC}</>)}
    </div>
  );
};

export default PowerHourTV;
